import { useEffect, useMemo, useState } from 'react'
import { Caption, COLORS, Panel, Badge } from './ui'

// Two ways of "seeing" the same image: instant human recognition vs a grid of numbers.
export function HumanVsCV2D() {
  const nums = useMemo(
    () => Array.from({ length: 8 * 8 }, () => Math.floor(Math.random() * 255)),
    []
  )
  const [mobile, setMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  const imgSize = mobile ? { width: 180, height: 135 } : { width: 220, height: 165 }
  const panelWidth = mobile ? '100%' : 300

  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto',
      alignItems: 'center', justifyContent: 'center', gap: mobile ? 4 : 8, padding: mobile ? '80px 20px 90px' : '90px 24px 110px',
      fontFamily: "'General Sans', system-ui, sans-serif", textAlign: 'center', color: COLORS.text,
    }}>
      <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', alignItems: mobile ? 'center' : 'stretch', justifyContent: 'center', gap: mobile ? 10 : 24, flexWrap: 'wrap', width: mobile ? '100%' : undefined, maxWidth: mobile ? 360 : undefined }}>
        {/* Human */}
        <Panel glow={COLORS.purple} style={{ width: panelWidth, padding: mobile ? 10 : 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: mobile ? 6 : 12 }}>
          <Badge color={COLORS.purple}>Human Vision</Badge>
          <img src="/cv/ref-cat.png" alt="cat" style={{ ...imgSize, objectFit: 'cover', borderRadius: 10 }} />
          <div style={{ fontSize: mobile ? 16 : 22, fontWeight: 700 }}>🐱 “A cat”</div>
          <div style={{ fontSize: mobile ? 12 : 14, color: COLORS.textDim, lineHeight: 1.5 }}>
            Instant recognition — the visual cortex labels the whole scene in ~100&nbsp;ms.
          </div>
        </Panel>

        {/* VS */}
        <div style={{ display: 'flex', alignItems: 'center', fontSize: mobile ? 13 : 18, fontWeight: 700, color: COLORS.textFaint }}>VS</div>

        {/* Computer */}
        <Panel glow={COLORS.indigo} style={{ width: panelWidth, padding: mobile ? 10 : 16, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: mobile ? 6 : 12 }}>
          <Badge color={COLORS.indigo}>Computer Vision</Badge>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: mobile ? 1 : 2,
            ...imgSize, fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: mobile ? 6 : 9,
          }}>
            {nums.map((n, i) => (
              <div key={i} style={{
                display: 'grid', placeItems: 'center', borderRadius: 2,
                color: `hsl(${(n / 255) * 50 + 220}, 80%, 70%)`,
                background: `hsl(${(n / 255) * 50 + 220}, 60%, ${10 + (n / 255) * 18}%)`,
              }}>{n}</div>
            ))}
          </div>
          <div style={{ fontSize: mobile ? 13 : 18, fontWeight: 700, fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: COLORS.indigoLight }}>[231, 201, 184]</div>
          <div style={{ fontSize: mobile ? 12 : 14, color: COLORS.textDim, lineHeight: 1.5 }}>
            Just numbers — a grid of brightness values, processed by maths.
          </div>
        </Panel>
      </div>

      <Caption>
        A computer never sees “a cat.” It sees a grid of numbers. Everything that follows is how we turn
        those numbers back into meaning.
      </Caption>
    </div>
  )
}
